/** @jsx isolateComponent */

import * as React from 'react';
import { cloneDeep, isArray } from 'lodash';
import { isolateComponent } from '@zapier/style-encapsulation';
import { extractParams } from './extractParams';

import type { HTMLAST } from './types';

type TransformFunction = (
  node: HTMLAST,
  // I had to update this from string | number to any because we have circular deps
  // that will break if I put it as the correct typing. We should fix those packages first
  // before coming back to this.
  key: any
) => React.ReactNode;

const isEmbedWidgetScript = (node: HTMLAST) => {
  return (
    node &&
    node.tagName === 'script' &&
    node.attrs &&
    node.attrs.src &&
    node.attrs.src.includes('/embed/widget.js')
  );
};

const parseEmbedWidgetSrc = (
  src: string
): { appSlugs: string; limit: string; zapIds: string } => {
  try {
    const { guided_zaps, services, limit } = extractParams(src);

    return {
      appSlugs: services && services.replace(/ /g, ''),
      limit,
      zapIds: guided_zaps && guided_zaps.replace(/ /g, ''),
    };
  } catch (e) {
    return {
      appSlugs: '',
      limit: '5',
      zapIds: '',
    };
  }
};

const isWistiaEmbedScript = (node: HTMLAST) => {
  return (
    node &&
    node.attrs &&
    node.attrs.src &&
    node.attrs.src.includes('https://fast.wistia.com/embed/medias/') &&
    node.attrs.src.includes('.jsonp')
  );
};

/**
 * Takes in the source of the `<script>` tag, and then extracts the filename
 * and finally removes the extension. What is left will be a Wistia video ID.
 */
const parseWistiaSrc = (src: string) => {
  try {
    const wistiaId = (src.split('/').pop() || '').replace('.jsonp', '');

    return wistiaId;
  } catch (e) {
    return '';
  }
};

const defaultTransform = (node: HTMLAST, key: string | number) => {
  // HTML TextNode
  if (node.rawText) {
    return node.rawText;
  }

  // fast-html-parser will adds a root node with a `null` tagName. The
  // equivalent of this in React is a Fragment.
  if (node.tagName === null && node.reactChildren) {
    return <React.Fragment key={key}>{node.reactChildren}</React.Fragment>;
  }

  // Every node which isn't a TextNode should have a tag name, but fail
  // gracefully in the event that it doesn't.
  if (!node.tagName) {
    return null;
  }

  // All other nodes should represent HTML elements.
  if (typeof node.tagName === 'string') {
    const Tag = node.tagName as keyof JSX.IntrinsicElements;
    return (
      <Tag key={key} {...node.attrs}>
        {node.reactChildren}
      </Tag>
    );
  }

  // Fail gracefully (null will instruct React to skip rendering this element).
  return null;
};

// Converts an HTML abstract syntax tree (AST, of the format output by the npm
// package fast-html-parser) into React elements, which can be rendered as part
// of a React view. Allows for custom override of the React elements to be
// used, so that custom React components can be inserted into the result.
const innerConvertHtmlAstToReactElements = (
  node: HTMLAST,
  transform: TransformFunction = defaultTransform,
  key: string
): React.ReactNode => {
  // Failsafe in case static typing lets us down. Application should not crash
  // if data is not passed.
  if (!node) {
    return null;
  }

  // Parses Zap template embeds into a format that is easily consumed
  // downstream. Consumers can take the IDs from the embed widget and pass
  // them off to a <ZapTemplateList /> component which will render the IDs
  // that are parsed from the embed widget URL.
  if (isEmbedWidgetScript(node)) {
    const { zapIds, appSlugs, limit } =
      node && node.attrs
        ? parseEmbedWidgetSrc(node.attrs.src)
        : { zapIds: '', appSlugs: '', limit: '' };

    const zapTemplateNode = {
      tagName: 'div',
      rawAttrs: `data-zap-template-list="true" data-zap-template-ids="${zapIds}" data-app-slugs="${appSlugs}" data-limit="${limit}"`,
      childNodes: [],
      classNames: [],
      attrs: {
        'data-zap-template-list': 'true',
        'data-zap-template-ids': zapIds,
        'data-app-slugs': appSlugs,
        'data-limit': limit,
      },
    };

    return typeof transform === 'function'
      ? transform(zapTemplateNode, key) ||
          defaultTransform(zapTemplateNode, key)
      : defaultTransform(zapTemplateNode, key);
  }

  // Parses Wistia embeds into a format that is easily consumed downstream.
  // A consumer can leverage the Wista Video ID and pass it over to the
  // <WistiaVideo> component.
  if (isWistiaEmbedScript(node)) {
    const wistiaVideoId =
      node && node.attrs && node.attrs.src
        ? parseWistiaSrc(node.attrs.src)
        : '';

    const wistiaVideoNode = {
      tagName: 'div',
      rawAttrs: `data-wistia-video="true" data-wistia-video-id="${wistiaVideoId}"`,
      childNodes: [],
      classNames: [],
      attrs: {
        'data-wistia-video': 'true',
        'data-wistia-video-id': wistiaVideoId,
      },
    };

    return typeof transform === 'function'
      ? transform(wistiaVideoNode, key) ||
          defaultTransform(wistiaVideoNode, key)
      : defaultTransform(wistiaVideoNode, key);
  }

  // The above Wistia script would be followed by a div that WistiaVideo will also add, so dropping it here
  if (
    node.tagName === 'div' &&
    node.classNames &&
    node.classNames.includes('wistia_responsive_padding')
  ) {
    return null;
  }

  // XSS prevention. This is not intended to replace output escaping for XSS.
  // This simply removes dangerous tags as early as possible. These tags are
  // intentionally never passed to the `transform` function so that developers
  // are unable to accidentally disable security.
  if (node.tagName && (node.tagName === 'script' || node.tagName === 'style')) {
    return null;
  }

  if (node.childNodes && isArray(node.childNodes)) {
    node.reactChildren = node.childNodes
      .map(
        (_node: React.ReactNode, index: number) =>
          _node &&
          innerConvertHtmlAstToReactElements(
            _node as HTMLAST,
            transform,
            `key-${index}`
          )
      )
      // Remove any elements which returned `null` to opt out of being rendered.
      .filter((element: any) => Boolean(element));

    // Matches the way that React handles children.
    const childrenCount = Array.isArray(node.reactChildren)
      ? node.reactChildren.length
      : 0;
    // It's not an array if there is only one child. If this logic is removed,
    // unit tests comparing React elements will fail with a "serializes to the
    // same string" error.
    if (childrenCount === 1) {
      node.reactChildren = (node.reactChildren as React.ReactNodeArray)[0];
    }
    // If you pass an empty array to a void element (which doesn't take any
    // children, e.g. `<img>`) then React will error.
    if (childrenCount === 0) {
      node.reactChildren = null;
    }
  }

  // We fall back to the default transformation function if the custom transform
  // does not specify a React element to replace with. This way the custom
  // transform doesn't need to account for every single HTML tag it might
  // encounter.
  //
  // It is very difficult to prove to Flow that something is a valid React element.
  return typeof transform === 'function'
    ? transform(node, key) || defaultTransform(node, key)
    : defaultTransform(node, key);
};

const convertHtmlAstToReactElements = (
  node: HTMLAST | React.ReactNode,
  transform?: TransformFunction,
  key = 'key-top'
) => {
  const clonedNode = cloneDeep(node);
  return innerConvertHtmlAstToReactElements(
    clonedNode as HTMLAST,
    transform,
    key
  );
};

export default convertHtmlAstToReactElements;
